import React, { createContext, useContext, useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { Coupon, CouponAssessment, CouponInput, CouponAssessmentInput, AssessmentField, RegenerateType } from '../types/generated.types';
import {
  addCouponMutation,
  updateCouponMutation,
  generateCouponSuggestionsMutation,
  getCouponsQuery,
  normalizeCouponPositionsMutation
} from '../queries/queries';
import { createEmptyCoupon, createEmptyCoupons, convertCouponsToState } from '../components/super-admin/companies/coupons/manage/utils/couponUtils';
import { ApprovalAction, ManageMode } from '../types/types';

export interface PendingApproval {
  suggestion: Partial<Coupon>;
  regenerateType: RegenerateType;
  assessmentFields?: AssessmentField[]; // Fields being regenerated (for ASSESSMENT_FIELDS type)
}

export interface CouponWithState {
  id: string;
  isEditing: boolean; // true if coupon is being edited, false if it's not
  isExisting: boolean; // true if coupon exists in database, false if it's new
  data: Partial<Coupon>;
  pendingApproval?: PendingApproval; // undefined = no pending approval
}

interface CouponHistory {
  past: Partial<Coupon>[];
  future: Partial<Coupon>[];
}

interface CouponsManageContextType {
  // State
  coupons: CouponWithState[];
  isSaving: boolean;
  isGenerating: boolean;
  error: string | null;
  // has unsaved changes
  isDirty: boolean;

  //History
  commitCouponHistory: (couponId: string) => void;
  undoCoupon: (couponId: string) => void;
  redoCoupon: (couponId: string) => void;
  canUndoCoupon: (couponId: string) => boolean;
  canRedoCoupon: (couponId: string) => boolean;

  // Actions
  initializeCoupons: (quantity: number, companyId: string | unknown, mode: ManageMode, propCoupons?: Coupon[]) => void;
  setCoupons: (newCoupons: CouponWithState[]) => void;
  toggleEdit: (couponId: string) => void;
  updateCouponDetails: (couponId: string, updates: Partial<Coupon>) => void;
  updateAssessment: (couponId: string, updates: Partial<CouponAssessment>) => void;
  removeCoupon: (couponId: string) => void;
  addEmptyCoupon: (companyId: string | unknown) => void;
  moveCouponUp: (couponId: string) => void;
  moveCouponDown: (couponId: string) => void;
  saveAllCoupons: () => Promise<boolean>;
  generateCouponSuggestions: (
    companyId: string | unknown,
    quantity: number,
    contexts?: string[],
    context?: string,
    couponId?: string,
    regenerateType?: RegenerateType,
    existingCouponData?: Partial<Coupon>,
    isExisting?: boolean,
    assessmentFields?: AssessmentField[]
  ) => Promise<void>;
  handleApprovalAction: (couponId: string, action: ApprovalAction) => Promise<void>;
  clearError: () => void;
}

const CouponsManageContext = createContext<CouponsManageContextType | undefined>(undefined);

interface CouponsManageProviderProps {
  children: React.ReactNode;
}

export const CouponsManageProvider: React.FC<CouponsManageProviderProps> = ({ children }) => {
  const [createCoupon, { loading: isCreating }] = useMutation(addCouponMutation);
  const [updateCoupon, { loading: isUpdating }] = useMutation(updateCouponMutation);
  const [generateCouponSuggestion, { loading: isGenerating }] = useMutation(generateCouponSuggestionsMutation);
  const [normalizeCouponPositions] = useMutation(normalizeCouponPositionsMutation);

  const [coupons, setCoupons] = useState<CouponWithState[]>([]);

  const [couponHistoryMap, setCouponHistoryMap] = useState<Map<string, CouponHistory>>(new Map());
  const [isDirty, setIsDirty] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const isSaving = isCreating || isUpdating;

  const commitCouponHistory = useCallback((couponId: string) => {
    setCoupons(prevCoupons => {
      setCouponHistoryMap(prev => {
        const newMap = new Map(prev);
        const history = newMap.get(couponId);
        if (!history) return prev;

        const coupon = prevCoupons.find(c => c.id === couponId);
        if (!coupon) return prev;

        const last = history.past[history.past.length - 1];

        if (JSON.stringify(last) === JSON.stringify(coupon.data)) {
          return prev;
        }

        const updatedPast = [...history.past, coupon.data];

        newMap.set(couponId, {
          past: updatedPast.length > 20
            ? updatedPast.slice(1)
            : updatedPast,
          future: []
        });

        setIsDirty(true);

        return newMap;
      });

      return prevCoupons;
    });
  }, []);

  const undoCoupon = (couponId: string) => {
    setCouponHistoryMap(prev => {
      const newMap = new Map(prev);
      const history = newMap.get(couponId);
      if (!history || history.past.length <= 1) return prev;

      const newPast = history.past.slice(0, -1);
      const undoneState = history.past[history.past.length - 1];

      newMap.set(couponId, {
        past: newPast,
        future: [undoneState, ...history.future]
      });

      // Apply new current state
      const newCurrent = newPast[newPast.length - 1];
      setCoupons(coupons =>
        coupons.map(c =>
          c.id === couponId
            ? { ...c, data: newCurrent }
            : c
        )
      );

      return newMap;
    });
  };

  const redoCoupon = (couponId: string) => {
    setCouponHistoryMap(prev => {
      const newMap = new Map(prev);
      const history = newMap.get(couponId);
      if (!history || history.future.length === 0) return prev;

      const redoState = history.future[0];

      newMap.set(couponId, {
        past: [...history.past, redoState],
        future: history.future.slice(1)
      });

      setCoupons(coupons =>
        coupons.map(c =>
          c.id === couponId
            ? { ...c, data: redoState }
            : c
        )
      );

      return newMap;
    });
  };

  const canUndoCoupon = (couponId: string) =>
    couponHistoryMap.size > 0 && (couponHistoryMap.get(couponId)?.past.length ?? 0) > 1;

  const canRedoCoupon = (couponId: string) =>
    couponHistoryMap.size > 0 && (couponHistoryMap.get(couponId)?.future.length ?? 0) > 0;


  // Initialize coupons based on the manage mode
  const initializeCoupons = useCallback((
    quantity: number,
    companyId: string | unknown,
    mode: ManageMode,
    propCoupons?: Coupon[]
  ) => {
    let initializedCoupons: CouponWithState[];

    switch (mode) {
      case ManageMode.EDIT:
        // Edit existing coupons - start in read-only mode
        if (!propCoupons || propCoupons.length === 0) {
          throw new Error('EDIT mode requires propCoupons');
        }
        initializedCoupons = convertCouponsToState(propCoupons, companyId, false);
        break;

      case ManageMode.ADD:
        // Create empty coupons in editing mode
        initializedCoupons = createEmptyCoupons(quantity, companyId);
        break;

      case ManageMode.GENERATE:
        // Generate mode: start with empty array, will be populated by generateCouponSuggestions
        initializedCoupons = [];
        break;

      default:
        throw new Error(`Unknown ManageMode: ${mode}`);
    }

    setCoupons(initializedCoupons);
  }, []);


  // Toggle the edit mode for a coupon
  const toggleEdit = useCallback((couponId: string) => {
    setCoupons(prev =>
      prev.map(coupon => {
        if (coupon.id !== couponId) return coupon;

        const nowEditing = !coupon.isEditing;

        // When entering edit mode, snapshot the current state
        if (nowEditing) {
          setCouponHistoryMap(prevMap => {
            const newMap = new Map(prevMap);

            newMap.set(couponId, {
              past: [coupon.data],
              future: []
            });

            return newMap;
          });
        }

        return {
          ...coupon,
          isEditing: nowEditing
        };
      })
    );
  }, []);

  // Update the coupon details
  const updateCouponDetails = useCallback((couponId: string, updates: Partial<Coupon>) => {
    setCoupons((prev) =>
      prev.map((coupon) =>
        coupon.id === couponId
          ? { ...coupon, data: { ...coupon.data, ...updates } }
          : coupon
      )
    );
    setIsDirty(true);
  }, []);

  // Update the coupon assessment
  const updateAssessment = useCallback((couponId: string, updates: Partial<CouponAssessment>) => {
    setCoupons((prev) =>
      prev.map((coupon) =>
        coupon.id === couponId
          ? {
            ...coupon,
            data: {
              ...coupon.data,
              assessment: {
                ...coupon.data.assessment,
                ...updates,
              } as CouponAssessment,
            },
          }
          : coupon
      )
    );
    setIsDirty(true);
  }, []);

  // Reorder coupon positions
  const moveCouponUp = useCallback((couponId: string) => {
    setCoupons((prev) => {
      const sorted = [...prev].sort((a, b) =>
        (a.data.position ?? 0) - (b.data.position ?? 0)
      );

      const index = sorted.findIndex(c => c.id === couponId);
      if (index <= 0) return prev;

      const newOrder = [...sorted];
      [newOrder[index - 1], newOrder[index]] =
        [newOrder[index], newOrder[index - 1]];

      return newOrder.map((coupon, i) => ({
        ...coupon,
        data: {
          ...coupon.data,
          position: i + 1
        }
      }));
    });

    setIsDirty(true);
  }, []);

  const moveCouponDown = useCallback((couponId: string) => {
    setCoupons((prev) => {
      const sorted = [...prev].sort((a, b) =>
        (a.data.position ?? 0) - (b.data.position ?? 0)
      );

      const index = sorted.findIndex(c => c.id === couponId);
      if (index === -1 || index >= sorted.length - 1) return prev;

      const newOrder = [...sorted];
      [newOrder[index], newOrder[index + 1]] =
        [newOrder[index + 1], newOrder[index]];

      return newOrder.map((coupon, i) => ({
        ...coupon,
        data: {
          ...coupon.data,
          position: i + 1
        }
      }));
    });

    setIsDirty(true);
  }, []);
  // --

  // Remove a coupon
  const removeCoupon = useCallback((couponId: string) => {
    setCoupons((prev) => prev.filter((coupon) => coupon.id !== couponId));
    setIsDirty(true);
  }, []);

  // Add an empty coupon in editing mode
  const addEmptyCoupon = useCallback((companyId: string | unknown) => {
    const newCoupon = createEmptyCoupon(companyId);
    const nextPosition = Math.max(...coupons.map(c => c.data.position ?? 0), 0) + 1;
    newCoupon.data.position = nextPosition;
    setCoupons((prev) => [...prev, newCoupon]);
    setIsDirty(true);
  }, [coupons]);

  // Save all coupons to the database
  const saveAllCoupons = useCallback(async () => {
    try {
      for (const coupon of coupons) {
        const { assessment, _id, ...couponData } = coupon.data;
        const assessmentInput = assessment
          ? {
            rating: assessment.rating,
            motivation: assessment.motivation,
            expectedEffect: assessment.expectedEffect,
            profitability: assessment.profitability,
            targetGroupAdaptation: assessment.targetGroupAdaptation,
            shouldShowAssessment: assessment.shouldShowAssessment ?? false,
          }
          : undefined;

        if (coupon.isExisting) {
          // Update existing coupon
          await updateCoupon({
            variables: {
              _id: _id!,
              discount: couponData.discount,
              smallTitle: couponData.smallTitle || '',
              description: couponData.description,
              terms: couponData.terms,
              codeType: couponData.codeType,
              code: couponData.code,
              estimatedSavings: couponData.estimatedSavings,
              imageURL: couponData.imageURL,
              productLink: couponData.productLink,
              position: couponData.position,
              assessment: assessmentInput,
              context: couponData.context,
            },
          });
        } else {
          // Create new coupon
          await createCoupon({
            variables: {
              companyId: couponData.companyId,
              discount: couponData.discount,
              smallTitle: couponData.smallTitle || '',
              description: couponData.description,
              terms: couponData.terms,
              codeType: couponData.codeType,
              code: couponData.code,
              estimatedSavings: couponData.estimatedSavings,
              imageURL: couponData.imageURL,
              productLink: couponData.productLink,
              position: couponData.position,
              assessment: assessmentInput,
              context: couponData.context,
            },
          });
        }
      }

      await normalizeCouponPositions({ variables: { companyId: coupons[0]?.data.companyId } });
      setIsDirty(false);
      return true;
    } catch (error) {
      console.error('Error saving coupons:', error);
      throw error;
    }
  }, [coupons, createCoupon, updateCoupon]);

  // Convert Coupon to CouponInput format
  const convertCouponToInput = useCallback((coupon: Partial<Coupon>): CouponInput => {
    const assessmentInput: CouponAssessmentInput | undefined = coupon.assessment
      ? {
        rating: coupon.assessment.rating ?? undefined,
        motivation: coupon.assessment.motivation ?? undefined,
        expectedEffect: coupon.assessment.expectedEffect ?? undefined,
        profitability: coupon.assessment.profitability ?? undefined,
        targetGroupAdaptation: coupon.assessment.targetGroupAdaptation ?? undefined,
        shouldShowAssessment: coupon.assessment.shouldShowAssessment ?? true,
      }
      : undefined;

    return {
      discount: coupon.discount ?? undefined,
      smallTitle: coupon.smallTitle ?? undefined,
      description: coupon.description ?? undefined,
      terms: coupon.terms ?? undefined,
      codeType: coupon.codeType ?? undefined,
      code: coupon.code ?? undefined,
      estimatedSavings: coupon.estimatedSavings ?? undefined,
      productLink: coupon.productLink ?? undefined,
      imageURL: coupon.imageURL ?? undefined,
      assessment: assessmentInput,
    };
  }, []);

  // Generate coupon suggestions with AI
  const generateCouponSuggestions = useCallback(async (
    companyId: string | unknown,
    quantity: number,
    contexts?: string[],
    context?: string,
    couponId?: string,
    regenerateType?: RegenerateType,
    existingCouponData?: Partial<Coupon>,
    isExisting?: boolean,
    assessmentFields?: AssessmentField[]
  ) => {
    try {
      setError(null); // Clear any previous errors

      // Convert existingCouponData to CouponInput format if provided
      const existingCouponDataInput = existingCouponData
        ? convertCouponToInput(existingCouponData)
        : undefined;

      // Only send couponId if the coupon exists in database (isExisting = true)
      // Use the _id from existingCouponData if it exists and isExisting is true
      const validCouponId = isExisting && existingCouponData?._id ? existingCouponData._id : undefined;

      const result = await generateCouponSuggestion({
        variables: {
          companyId,
          quantity,
          contexts,
          context: context || undefined,
          couponId: validCouponId,
          regenerateType: regenerateType || undefined,
          existingCouponData: existingCouponDataInput || undefined,
          assessmentFields: assessmentFields || undefined,
        },
        refetchQueries: [
          {
            query: getCouponsQuery,
            variables: {
              companyId,
            },
          },
        ],
      });

      if (result.data?.generateCouponSuggestions && result.data.generateCouponSuggestions.length > 0) {
        const generatedCoupons = result.data.generateCouponSuggestions;

        if (couponId && regenerateType) {
          // Regeneration: set pending approval on the specific coupon
          if (generatedCoupons.length > 0) {
            const generatedCoupon = generatedCoupons[0];
            setCoupons((prev) =>
              prev.map((coupon) =>
                coupon.id === couponId
                  ? {
                    ...coupon,
                    pendingApproval: {
                      suggestion: generatedCoupon,
                      regenerateType,
                      assessmentFields: assessmentFields || undefined,
                    },
                  }
                  : coupon
              )
            );
          }
        } else if (couponId) {
          // Legacy: Update a specific coupon directly
          if (generatedCoupons.length > 0) {
            const generatedCoupon = generatedCoupons[0];
            setCoupons((prev) =>
              prev.map((coupon) =>
                coupon.id === couponId
                  ? {
                    ...coupon,
                    data: {
                      ...coupon.data,
                      discount: generatedCoupon.discount || coupon.data.discount,
                      smallTitle: generatedCoupon.smallTitle || coupon.data.smallTitle,
                      description: generatedCoupon.description || coupon.data.description,
                      terms: generatedCoupon.terms || coupon.data.terms,
                      codeType: generatedCoupon.codeType || coupon.data.codeType,
                      code: generatedCoupon.code || coupon.data.code,
                      estimatedSavings: generatedCoupon.estimatedSavings || coupon.data.estimatedSavings,
                      productLink: generatedCoupon.productLink || coupon.data.productLink,
                      assessment: generatedCoupon.assessment || coupon.data.assessment,
                    }
                  }
                  : coupon
              )
            );
          }
        } else {
          // If quantity is 1, check if we should add to existing or replace all
          setCoupons((prev) => {
            // If quantity is 1 and we already have coupons, add the new one instead of replacing
            if (quantity === 1 && prev.length > 0) {
              const nextPosition = Math.max(...prev.map(c => c.data.position ?? 0), 0) + 1;

              // Add new generated coupon to existing list
              const newCoupon: CouponWithState = {
                id: `generated-coupon-${Date.now()}`,
                isEditing: false,
                isExisting: false,
                data: {
                  ...generatedCoupons[0],
                  companyId,
                  position: nextPosition,
                }
              };
              return [...prev, newCoupon];
            } else {
              // Initial generation: Replace all coupons with generated ones
              const newCoupons: CouponWithState[] = generatedCoupons.map((generatedCoupon, index) => ({
                id: `generated-coupon-${index}`,
                isEditing: false,
                isExisting: false,
                data: {
                  ...generatedCoupon,
                  companyId,
                  position: index,
                }
              }));
              return newCoupons;
            }
          });
        }

        setIsDirty(true);
      }
    } catch (error) {
      console.error('Error generating coupon suggestions:', error);

      // Extract error message from GraphQL error
      let errorMessage = 'Ett fel uppstod vid generering av kuponger.';
      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        const graphQLError = error as { graphQLErrors?: Array<{ message: string }> };
        if (graphQLError.graphQLErrors && graphQLError.graphQLErrors.length > 0) {
          errorMessage = graphQLError.graphQLErrors[0].message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      throw error;
    }
  }, [generateCouponSuggestion, convertCouponToInput]);

  // Handle approval actions
  const handleApprovalAction = useCallback(async (couponId: string, action: ApprovalAction) => {
    const coupon = coupons.find((c) => c.id === couponId);
    if (!coupon || !coupon.pendingApproval) {
      return;
    }

    const { suggestion, regenerateType, assessmentFields } = coupon.pendingApproval;

    if (action === ApprovalAction.REJECT) {
      // Remove pending approval, keep original coupon
      setCoupons((prev) =>
        prev.map((c) =>
          c.id === couponId
            ? { ...c, pendingApproval: undefined }
            : c
        )
      );
    } else if (action === ApprovalAction.APPROVE) {
      // Replace coupon data with pending suggestion and remove pending approval
      setCoupons((prev) =>
        prev.map((c) =>
          c.id === couponId
            ? {
              ...c,
              data: {
                ...c.data,
                discount: suggestion.discount || c.data.discount,
                smallTitle: suggestion.smallTitle || c.data.smallTitle,
                description: suggestion.description || c.data.description,
                terms: suggestion.terms || c.data.terms,
                codeType: suggestion.codeType || c.data.codeType,
                code: suggestion.code || c.data.code,
                estimatedSavings: suggestion.estimatedSavings || c.data.estimatedSavings,
                productLink: suggestion.productLink || c.data.productLink,
                assessment: suggestion.assessment || c.data.assessment,
              },
              pendingApproval: undefined,
            }
            : c
        )
      );
      setIsDirty(true);
    } else if (action === ApprovalAction.REGENERATE) {
      // Regenerate again with same type
      if (coupon.data.companyId && regenerateType) {
        await generateCouponSuggestions(
          coupon.data.companyId,
          1,
          undefined,
          coupon.data.context || '',
          couponId,
          regenerateType,
          coupon.data,
          coupon.isExisting,
          assessmentFields
        );
      }
    }
  }, [coupons, generateCouponSuggestions]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const contextValue: CouponsManageContextType = {
    coupons,
    isSaving,
    isGenerating,
    error,
    isDirty,
    initializeCoupons,
    setCoupons,
    toggleEdit,
    updateCouponDetails,
    updateAssessment,
    removeCoupon,
    addEmptyCoupon,
    saveAllCoupons,
    moveCouponDown,
    moveCouponUp,
    generateCouponSuggestions,
    handleApprovalAction,
    clearError,
    commitCouponHistory,
    undoCoupon,
    redoCoupon,
    canUndoCoupon,
    canRedoCoupon
  };

  return (
    <CouponsManageContext.Provider value={contextValue}>
      {children}
    </CouponsManageContext.Provider>
  );
};

export const useCouponsManageContext = (): CouponsManageContextType => {
  const context = useContext(CouponsManageContext);
  if (context === undefined) {
    throw new Error('useCouponsManageContext must be used within a CouponsManageProvider');
  }
  return context;
};

